import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '@/components/Card';
import { FormManagementProps } from '@/type/form.types';
import { useGetChildTab } from '@/hooks/tab';
import { GroupWhiteLabelTab } from '@/type/ui/tab/groupTab';
import { TabItem } from '@/components/Tab/TabItem';
import { TabList } from '@/components/Tab/TabList';
import { GroupWhiteLabelInfosManagement } from './GroupWhiteLabelInfosManagement';
import { twMerge } from 'tailwind-merge';
import { FieldValues } from 'react-hook-form';
import { GroupWhiteLabelAppManagement } from './GroupWhiteLabelAppManagement';
import { TitleSection } from '@/components/Header/Section/TitleSection';

export const GroupWhiteLabelManagement = <TSchema extends FieldValues>({
  t,
  data,
  watch,
  control,
  register,
  setValue,
  setError,
  errors,
  submitDisabled,
  onSubmit,
}: FormManagementProps<TSchema>) => {
  const navigate = useNavigate();

  const { currentTab, navigateToTab } = useGetChildTab({
    defaultTab: GroupWhiteLabelTab.INFOS,
  });

  useEffect(() => {
    if (data && !data?.isWhiteLabel) {
      navigate('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <Card
      title={
        <TitleSection
          text={t('common:whiteLabel')}
          defaultIconClassName="ki-copy-success opacity-70"
        />
      }
      cardClassName="mb-5 bg-slate-100/65"
      bodyClassName="py-8 flex flex-col"
    >
      <div>
        <TabList className="mb-7">
          <TabItem
            className={twMerge(
              'border-black/50',
              currentTab !== GroupWhiteLabelTab.INFOS && 'border-black',
            )}
            text={t('common:informations')}
            leftIcon={<i className="ki-outline ki-file-sheet" />}
            active={currentTab === GroupWhiteLabelTab.INFOS}
            onChange={() => navigateToTab(GroupWhiteLabelTab.INFOS)}
          />
          <TabItem
            className={twMerge(
              'border-black/50',
              currentTab !== GroupWhiteLabelTab.MOBILE_APP && 'border-black',
            )}
            text={t('common:mobileApp')}
            leftIcon={<i className="mt-[0.5px] ki-outline ki-pad" />}
            active={currentTab === GroupWhiteLabelTab.MOBILE_APP}
            onChange={() => navigateToTab(GroupWhiteLabelTab.MOBILE_APP)}
          />
        </TabList>
      </div>
      <div className="space-y-9">
        {currentTab === GroupWhiteLabelTab.INFOS && (
          <GroupWhiteLabelInfosManagement<TSchema>
            t={t}
            data={data}
            watch={watch}
            control={control}
            register={register}
            setValue={setValue}
            setError={setError}
            errors={errors}
            submitDisabled={submitDisabled}
            onSubmit={onSubmit}
          />
        )}
        {currentTab === GroupWhiteLabelTab.MOBILE_APP && (
          <GroupWhiteLabelAppManagement<TSchema>
            t={t}
            data={data}
            watch={watch}
            control={control}
            register={register}
            setValue={setValue}
            setError={setError}
            errors={errors}
            submitDisabled={submitDisabled}
            onSubmit={onSubmit}
          />
        )}
      </div>
    </Card>
  );
};
